<template>
    <div>
        <p v-if="error" class="text-danger">{{ errorMsg }}</p>
        <div>
            <b-card 
                id="response-history-table-card"
                class="shadow rounded-lg"
                no-body
            >
                <div
                    v-if="!loading && !responses.length"
                    class="d-flex justify-content-center align-items-center"
                    style="min-height: 300px;"
                >
                    <div class="text-center">
                        <div class="font-size-100 text-muted mb-4">
                            This vendor has no past Sourcing responses.
                        </div>
                    </div>
                </div>

                <b-table 
                    v-else 
                    :items="responses"
                    :fields="fields"
                    :busy="loading"
                    primary-key="id" 
                    :per-page="perPage"
                    :current-page="currentPage"
                    @sort-changed="onSortChanged"
                    class="no-top-border td-align-middle mb-0"
                    thead-class="overflow-hidden"
                    head-variant="light"
                >
                    <template v-slot:table-busy>
                        <busy-spinner></busy-spinner>
                    </template>
                    <template #cell(rfx_title)="data">
                        <b-link v-if="hasAccess(data.item)" key="isOnRfxTeam" :href="data.item.manage_response_url" class="font-weight-500">{{data.value}}</b-link>
                        <span v-else key="notOnRfxTeam" class="font-weight-500">{{data.value}}</span>
                        <b-badge 
                            v-if="!hasAccess(data.item)" 
                            v-b-tooltip.hover
                            title="You do not have access to the detils of this RFx"
                            variant="soft-secondary" 
                            class="text-uppercase ml-2"
                        >No Access</b-badge>
                    </template>
                    <template #cell(sm_state)="data">
                        <status-object-badge
                            :status-object="data.item.state_dict"
                            text-uppercase
                            no-tooltip
                        ></status-object-badge>
                    </template>
                    <template #cell(rfx_sm_state)="data">
                        <status-object-badge
                            :status-object="data.item.rfx_state_dict"
                            text-uppercase
                            no-tooltip
                        ></status-object-badge>
                    </template>
                </b-table>
                <template #footer v-if="!loading && responses.length">
                    <base-table-pagination
                        :current-page.sync="currentPage"
                        :per-page.sync="perPage"
                        :total-items="responses.length"
                        show-per-page
                    ></base-table-pagination>
                </template>
            </b-card>
        </div>
    </div>
</template>

<script>
import { suppliosApi } from '../js/api-service';
import BaseTablePagination from './BaseTablePagination';
// import { FormatDateHelper } from '../js/filters-helpers';
import StatusObjectBadge from '../components/functional-comps/StatusObjectBadge';
// import { RfxResponse } from '../js/models/rfxResponse';
import { currentUserInfoMixin } from '../js/custom-vue-mixins';
import { DateTime } from 'luxon';
/* This is for INTERNAL views of Company RfxResponse History */

class LocalRfxResponse {
    constructor({  // we keep this local to keep our sanity... and because we return a custom object from backend
        id,
        created,
        modified,
        rfx_id,
        rfx_title,
        rfx_sm_state,
        rfx_state_dict,
        sourcing_owner_id,
        sourcing_owner_name,
        sourcing_category_name, 
        sm_state,
        state_dict,
        invitation_date,
        last_submitted_on,
        submitted_by_name,  // removing?  refer to Submission model
        manage_response_url,
        can_view,
    }) {
        // make sure to set defaults if they won't always be there, and undefined isn't cool!
        this.id = id;
        this.created = created ? DateTime.fromISO(created) : null;
        this.modified = modified ? DateTime.fromISO(modified) : null;
        this.rfx_id = rfx_id || null;  // id of the RFx
        this.rfx_title = rfx_title || '';  // keep empty string if not set for sorting
        this.rfx_sm_state = rfx_sm_state || '';
        this.rfx_state_dict = rfx_state_dict || {};
        this.sourcing_category_name = sourcing_category_name || '';  // keep empty string if not set for sorting
        this.sourcing_owner_id = sourcing_owner_id || null;  // id of the sourcing owner
        this.sourcing_owner_name = sourcing_owner_name || '';  // keep empty string if not set for sorting
        this.sm_state = sm_state || '';
        this.state_dict = state_dict || {};
        const invitation_date_luxon = invitation_date ? DateTime.fromISO(invitation_date) : null;
        this.invitation_date_formatted = invitation_date_luxon ? invitation_date_luxon.toLocaleString(DateTime.DATETIME_MED) : '-';
        this.invitation_date_timestamp = invitation_date_luxon ? invitation_date_luxon.toMillis() : null;
        const last_submitted_on_luxon = last_submitted_on ? DateTime.fromISO(last_submitted_on) : null;
        this.last_submitted_on_formatted = last_submitted_on_luxon ? last_submitted_on_luxon.toLocaleString(DateTime.DATETIME_MED) : '-';
        this.last_submitted_on_timestamp = last_submitted_on_luxon ? last_submitted_on_luxon.toMillis() : null;
        this.submitted_by_name = submitted_by_name || '';  // keep empty string if not set for sorting
        this.manage_response_url = manage_response_url || null;  // url to manage response
        this.can_view = can_view || false;  // whether the user can view this response
    }
}


export default {
    name: 'CompanyRfxResponseHistory',
    components: {
        BaseTablePagination,
        StatusObjectBadge,
    },
    mixins: [
        currentUserInfoMixin,
    ],
    props: {
        companyId: {
            type: [Number, String],
            required: true,
        }
    },
    data: function() {
        return {
            errorMsg: 'Error',
            error: false,
            loading: false,
            responses: [],
            currentPage: 1,
            perPage: 10,
            fields: [
                {
                    key: 'rfx_title',
                    label: 'RFx Title',
                    sortable: true,
                },
                {
                    key: 'rfx_sm_state',
                    label: 'RFx Status',
                    sortable: true,
                },
                {
                    key: 'sourcing_owner_name',
                    label: 'Event Owner',
                    sortable: true,
                },
                {
                    key: 'sm_state',  // use sm_state as key, but will display state_dict (badge)
                    label: 'Status',
                    sortable: true,
                },
                {
                    key: 'invitation_date_timestamp',
                    label: 'Invited Date',
                    sortable: true,
                    formatter: (value, key, item) => {
                        return item.invitation_date_formatted;  // use the formatted value for display
                    },
                    sortByFormatted: false,  
                },
                {
                    key: 'last_submitted_on_timestamp',
                    label: 'Submitted Date',
                    sortable: true,
                    formatter: (value, key, item) => {
                        return item.last_submitted_on_formatted;  // use the formatted value for display
                    },
                    sortByFormatted: false,  
                },
                {
                    key: 'submitted_by_name',
                    label: 'Submitted By',
                    sortable: true,
                }
            ]
        }
    },
    computed: {
        currentTuCanViewAll() {
            return this.$currentUserInfo.user_is_staff || this.$currentUserInfo.is_tenant_admin || this.$currentUserInfo.is_tenant_superadmin;
        },
    },
    mounted() {
        this.getResponses();
    },
    methods: {
        refresh() {
            // called from parent components, so do not change this without checking those
            this.getResponses();
        },
        handleError(error, message) {
            console.log(message);
            console.log(error);
            this.errorMsg = message;
            this.error = true;
        },
        async getResponses() {
            this.loading = true;
            let resp;
            try {
                resp = await suppliosApi.vendorStandard.get_rfx_response_history(this.companyId);  // only fetches a SUBSET of fields, not all fields from a field_group
            } catch (error) {
                this.handleError(error, 'Error while loading list of responses')
            }
            if (resp?.data) {
                // console.log('Got response history, response below');
                // console.log(resp);
                this.responses = resp.data.map(r => new LocalRfxResponse(r));  // maybe don't make a full RfxResponse object, because we don't get all the fields from backend anyways!
                // NOTE: our datetime fields (invitation_date, last_submitted_on) will be Luxon DateTime objects in RfxResponse objects, or null if not set
                // console.log('Response history, after mapping to LocalRfxResponse objects');
                // console.log(this.responses);
            }
            this.loading = false;
            // suppliosApi.vendorStandard.get_rfx_response_history(this.companyId)  // only fetches a SUBSET of fields, not all fields from a field_group
            // .then(response => {
            //     console.log('Got response history, response below');
            //     console.log(response);
            //     this.responses = response.data.map(resp => new RfxResponse(resp));  // maybe don't make a full RfxResponse object, because we don't get all the fields from backend anyways!
            //     // NOTE: our datetime fields (invitation_date, last_submitted_on) will be Luxon DateTime objects in RfxResponse objects, or null if not set
            // })
            // .catch(error => this.handleError(error, 'Error while loading list of responses'))
            // .finally(() => this.loading = false)
        },
        hasAccess(resp) {
            // pass in a RfxResponse instance, return boolean for whether this user can view / has access
            return !!resp.can_view || this.currentTuCanViewAll;  // TODO - add a .can_read attribute on backend that also allows admin/superadmins, or any other logic appropriate
            // TODO - add a .can_read attribute on backend that also allows admin/superadmins, or any other logic appropriate
        },
        onSortChanged(ctx) {
            // console.log('sort changed, ctx below');
            // console.log(ctx);
            // ctx has: sortBy (field key), sortDesc (bool), currentPage, perPage, filter, apiUrl
            // but all we need to do is change to currentPage = 1 if not already set
            const { currentPage } = ctx;
            if (currentPage !== 1) {
                this.currentPage = 1;
            }
        },
    }
}
</script>

<style>

</style>